<template>
    <modal-box v-model="isModalActive" :title="__('Please confirm action')" has-cancel @confirm="confirmDeletion">
        <p>{{ __('Are you sure you want to delete this resource?') }}</p>
    </modal-box>

    <modal-box v-model="isGenerationModalActive" :title="__('Please wait')" :has-control="false">
        <p>{{
                __('Your :resource is generated, please do not refresh the page', {resource: __('The Price').toLowerCase()})
            }}</p>
        <p class="flex justify-center pt-2">
            <!-- By Sam Herbert (@sherb), for everyone. More @ http://goo.gl/7AJzbL -->
            <svg xmlns="http://www.w3.org/2000/svg" width="60" height="15" viewBox="0 0 120 30" fill="#3b82f6">
                <circle cx="15" cy="15" r="15">
                    <animate attributeName="r" from="15" to="15" begin="0s" dur="0.8s" values="15;9;15"
                             calcMode="linear" repeatCount="indefinite"/>
                    <animate attributeName="fill-opacity" from="1" to="1" begin="0s" dur="0.8s" values="1;.5;1"
                             calcMode="linear" repeatCount="indefinite"/>
                </circle>
                <circle cx="60" cy="15" r="9" fill-opacity="0.3">
                    <animate attributeName="r" from="9" to="9" begin="0s" dur="0.8s" values="9;15;9" calcMode="linear"
                             repeatCount="indefinite"/>
                    <animate attributeName="fill-opacity" from="0.5" to="0.5" begin="0s" dur="0.8s" values=".5;1;.5"
                             calcMode="linear" repeatCount="indefinite"/>
                </circle>
                <circle cx="105" cy="15" r="15">
                    <animate attributeName="r" from="15" to="15" begin="0s" dur="0.8s" values="15;9;15"
                             calcMode="linear" repeatCount="indefinite"/>
                    <animate attributeName="fill-opacity" from="1" to="1" begin="0s" dur="0.8s" values="1;.5;1"
                             calcMode="linear" repeatCount="indefinite"/>
                </circle>
            </svg>
        </p>
    </modal-box>

    <div v-if="checkedRows.length" class="bg-gray-50 p-3">
        <span v-for="checkedRow in checkedRows" :key="checkedRow.id"
              class="inline-block bg-gray-100 px-2 py-1 rounded-sm mr-2 text-sm">{{ checkedRow.name }}</span>
    </div>

    <table class="price-config-table overflow-hidden">
        <thead>
        <tr>
            <th v-if="checkable"></th>
            <th :class="sorTableClass('id')" @click.prevent="sortTable('id')">
                <div class="flex items-center">
                    <span>{{ __('ID') }}</span>
                </div>
            </th>
            <th :class="sorTableClass('user.name')" @click.prevent="sortTable('user.name')">
                <div class="flex items-center">
                    <span>{{ __('Author') }}</span>
                </div>
            </th>
            <th :class="sorTableClass('name')" @click.prevent="sortTable('name')">
                <div class="flex items-center">
                    <span>{{ __('Client') }}</span>
                </div>
            </th>
            <th :class="sorTableClass('file_format')" @click.prevent="sortTable('file_format')">
                <div class="flex items-center">
                    <span>{{ __('Format') }}</span>
                </div>
            </th>
            <th :class="sorTableClass('price_language')" @click.prevent="sortTable('price_language')">
                <div class="flex items-center">
                    <span>{{ __('Language') }}</span>
                </div>
            </th>
            <th :class="sorTableClass('exchange_changes')" @click.prevent="sortTable('exchange_changes')">
                <div class="flex items-center">
                    <span>{{ __('Update when the exchange rate changes') }}</span>
                </div>
            </th>
            <th :class="sorTableClass('usd_currency')" @click.prevent="sortTable('usd_currency')">
                <div class="flex items-center">
                    <span>{{ __('USD Currency') }}</span>
                </div>
            </th>
            <th :class="sorTableClass('site.host')" @click.prevent="sortTable('site.host')">
                <div class="flex items-center">
                    <span>{{ __('Site') }}</span>
                </div>
            </th>
            <th :class="sorTableClass('update_interval')" @click.prevent="sortTable('update_interval')">
                <div class="flex items-center">
                    <span>{{ __('Interval') }}</span>
                </div>
            </th>
            <th :class="sorTableClass('generated_at')" @click.prevent="sortTable('generated_at')">
                <div class="flex items-center">
                    <span>{{ __('Last update') }}</span>
                </div>
            </th>
            <th :class="sorTableClass('updated_at')" @click.prevent="sortTable('updated_at')">
                <div class="flex items-center">
                    <span>{{ __('Edited') }}</span>
                </div>
            </th>
            <th></th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="priceConfig in itemsPaginated" :key="priceConfig.id">
            <checkbox-cell v-if="checkable" @checked="checked($event, priceConfig)"/>
            <td :data-label="__('ID')">{{ priceConfig.id }}</td>
            <td class="table-author" :data-label="__('Author')">
                <span class="block">
                    {{ priceConfig.user.name }}
                </span>
                <small class="text-gray-500" :title="priceConfig.created_at">
                    {{ moment(priceConfig.created_at).locale('uk').format('DD.MM.YYYY HH:mm') }}
                </small>
            </td>
            <td :data-label="__('Client')">{{ priceConfig.name }}</td>
            <td :data-label="__('Format')">{{ priceConfig.file_format }}</td>
            <td :data-label="__('Language')">{{ __(getLanguageLabel(priceConfig.price_language)) }}</td>
            <td :data-label="__('Update when the exchange rate changes')">
                {{ priceConfig.exchange_changes ? __('Yes') : __('No') }}
            </td>
            <td :data-label="__('USD Currency')">{{ priceConfig.usd_currency ? __('Yes') : __('No') }}</td>
            <td :data-label="__('Site')">{{ priceConfig.site !== null ? priceConfig.site.host : null }}</td>
            <td :data-label="__('Interval')">
                <small class="text-gray-500" v-if="priceConfig.update_interval > 0"
                       :title="priceConfig.update_interval">
                    {{ priceConfig.update_interval }} {{ __('per day|per day|per day', priceConfig.update_interval) }}
                </small>
                <small class="text-gray-500" v-else :title="priceConfig.update_interval">
                    {{ __('None') }}
                </small>
            </td>
            <td :data-label="__('Last update')">
                <small v-if="priceConfig.generated_at" class="text-gray-500" :title="priceConfig.generated_at">
                    {{ moment(priceConfig.generated_at).locale('uk').calendar() }}
                </small>
                <small v-else class="text-gray-500" :title="priceConfig.generated_at">
                    {{ __('Never') }}
                </small>
            </td>

            <td class="table-edited" :data-label="__('Edited')">
                <small v-if="priceConfig.config_versions?.length" class="text-gray-500" :title="priceConfig.updated_at">
                    {{ priceConfig.config_versions.slice(-1)[0].user.name }}<br>
                    {{ moment(priceConfig.updated_at).locale('uk').format('DD.MM.YYYY HH:mm') }}
                </small>
                <small v-else class="text-gray-500" :title="priceConfig.updated_at">
                    {{ priceConfig.user.name }}<br>
                    {{ moment(priceConfig.updated_at).locale('uk').format('DD.MM.YYYY HH:mm') }}
                </small>
            </td>

            <td class="actions-cell">
                <input type="text" class="price-link" :data-id="priceConfig.id"
                       :value="route('price-configs.show', priceConfig.hash)">

                <div class="flex items-center justify-start lg:justify-end flex-nowrap -mb-3">
                    <tooltip v-if="priceConfig.file_format !== 'xlsx'" :text="__('Copied')" class="mr-2"
                             :data-id="priceConfig.id">
                        <jb-button color="warning" :icon="mdiLinkVariant" small
                                   @click.prevent="copyPriceLink(priceConfig.id)" target="_blank"/>
                    </tooltip>
                    <jb-button class="mr-2" color="success" :icon="mdiPencil" small
                               @click.prevent="$inertia.visit(route('price-configs.edit', priceConfig.id))"/>
                    <jb-button class="mr-2" color="info" :icon="mdiDownload" small
                               @click.prevent="downloadPrice(priceConfig)"/>
                    <jb-button color="danger" :icon="mdiTrashCan" small
                               @click="deleteItemConfirmation(priceConfig.id)"/>
                </div>
            </td>
        </tr>
        </tbody>
    </table>
    <pagination
        :current-page="currentPage"
        :total-pages="numPages"
        @page-changed="currentPage = $event"
    />

    <div v-if="isUserHasAdminRole()" class="py-4 px-5">
        <jb-button v-if="isRunningUpdatePricesJob === false" color="danger" @click="runUpdatePriceJob()">
            {{ __('Resave existing prices') }}
        </jb-button>
        <jb-button v-else color="gray" disabled>
            {{ __('Resave existing prices in progress') }}
        </jb-button>
    </div>

    <iframe id="download" style="display:none;"></iframe>
</template>

<style>
@media screen and (min-width: 1280px) {
    .price-config-table th,
    .price-config-table td {
        font-size: 14px;
    }
}
.price-link {
    pointer-events: none;
    position: absolute;
    opacity: 0;
}

table thead th {
    cursor: pointer;
}

.can-sort {
    position: relative;
}

.can-sort > div:after {
    content: "↑";
    display: inline-block;
    margin-left: 5px;
    position: relative;
    opacity: 0;
}

.can-sort.asc > div:after {
    content: "↓";
}

.can-sort.sortable > div:after {
    opacity: 1;
}
</style>

<script>
import {computed, ref} from 'vue'
import {mdiEye, mdiTrashCan, mdiPencil, mdiDownload, mdiLinkVariant, mdiChevronLeft, mdiChevronRight} from '@mdi/js'
import ModalBox from "@/Components/Dashboard/ModalBox";
import CheckboxCell from '@/Components/Dashboard/CheckboxCell'
import Level from '@/Components/Dashboard/Level'
import JbButtons from '@/Components/Dashboard/JbButtons'
import JbButton from '@/Components/Dashboard/JbButton'
import moment from 'moment'
import __ from '@/trans'
import Tooltip from "@/Components/Dashboard/Tooltip";
import Pagination from "@/Components/Dashboard/Pagination.vue";

export default {
    name: 'PriceConfigsTable',
    components: {
        Pagination,
        Tooltip,
        ModalBox,
        CheckboxCell,
        Level,
        JbButtons,
        JbButton
    },
    props: {
        checkable: Boolean,
        items: Object,
        languageOptions: Array,
        search: String
    },
    data() {
        return {
            sortColumn: 'id',
            ascending: false,
            statusCheckInterval: null
        }
    },
    mounted() {
        document.getElementById('download').removeAttribute('src');
        
        this.checkJobStatus(isRunning => {
            if(isRunning) {
                this.startStatusChecking()
            }
        })
    },
    beforeUnmount() {
        // Очищаємо інтервал при знищенні компонента
        this.stopStatusChecking()
    },
    watch: {
        search(newVal, oldVal) {
            this.currentPage = 0
        }
    },
    methods: {
        isUserHasAdminRole()
        {
            return this.$page.props.auth.user.roles.filter(role => role.name === 'admin').length > 0
        },
        runUpdatePriceJob()
        {
            if(!confirm(__('Are you sure you want to run this action?'))) {
                return
            }

            axios.post(this.route('api.v1.price-configs.update-prices')).then(response => {
                this.isRunningUpdatePricesJob = true
                this.startStatusChecking()
            });
        },
        startStatusChecking() {
            // Перевіряємо статус кожні 5 секунд
            this.statusCheckInterval = setInterval(() => {
                this.checkJobStatus()
            }, 5000)
        },
        stopStatusChecking() {
            if (this.statusCheckInterval) {
                clearInterval(this.statusCheckInterval)
                this.statusCheckInterval = null
            }
        },
        checkJobStatus(callback = null) {
            axios.get(this.route('api.v1.price-configs.update-prices-status'))
                .then(response => {
                    this.isRunningUpdatePricesJob = response.data.isRunning
                    
                    // Якщо job завершилася - зупиняємо перевірку
                    if (!response.data.isRunning) {
                        this.stopStatusChecking()
                    }

                    if (callback) {
                        callback(response.data.isRunning)
                    }
                })
                .catch(error => {
                    console.error('Error checking job status:', error)
                    // У разі помилки також зупиняємо перевірку
                    this.stopStatusChecking()
                    this.isRunningUpdatePricesJob = false
                })
        },
        copyPriceLink(id) {
            let input = document.querySelector(`.price-link[data-id="${id}"]`)
            input.select()
            document.execCommand("copy")

            this.showCopiedTooltip(id)
        },
        showCopiedTooltip(id) {
            let tooltip = document.querySelector(`.tooltip-box[data-id="${id}"] .tooltip`)
            if (tooltip === null) {
                return
            }

            tooltip.classList.remove('hidden')
            setTimeout(() => {
                tooltip.classList.add('hidden')
            }, 2000)
        },
        downloadPrice(priceConfig) {
            const self = this
            let needModal = true

            setTimeout(() => {
                if (needModal) {
                    self.isGenerationModalActive = true
                }
            }, 500)

            axios.post(this.route('api.v1.price-configs.async-download'), {
                'hash': priceConfig.hash
            }).then(response => {
                needModal = false
                setTimeout(() => {
                    self.isGenerationModalActive = false
                }, 1000)
                document.getElementById('download').src = self.route('price-configs.download', priceConfig.hash);
            }).then(error => {
                self.isGenerationModalActive = false
            });
        },
        sorTableClass(col) {
            return {
                'can-sort': true,
                'sortable': this.sortColumn === col,
                'asc': this.sortColumn === col && this.ascending
            }
        },
        sortTable(col) {
            if (this.sortColumn === col) {
                this.ascending = !this.ascending
            } else {
                this.ascending = true
                this.sortColumn = col
            }

            this.sortItems()
        },
        sortItems() {
            const self = this
            const sortColumn = this.sortColumn.split('.')

            this.items.sort(function (a, b) {
                let aValue = a, bValue = b;

                sortColumn.forEach(key => {
                    aValue = aValue[key]
                    bValue = bValue[key]
                });

                if (aValue > bValue) {
                    return self.ascending ? 1 : -1
                } else if (aValue < bValue) {
                    return self.ascending ? -1 : 1
                }
                return 0
            })
        },
        deleteItemConfirmation(itemId) {
            this.isModalActive = true
            this.itemForDeleteId = itemId
        },
        confirmDeletion() {
            this.$inertia.form().delete(this.route('price-configs.destroy', this.itemForDeleteId), {
                onFinish: () => this.itemForDeleteId = false
            })
        }
    },
    setup(props) {

        const getLanguageLabel = (code) => {
            const language = props.languageOptions.find(item => item.id == code)
            return typeof language === "undefined" ? code : language.label;
        }

        const items = computed(() => props.items.filter(item => {
            const exchange_changes = item.exchange_changes ? __('Yes') : __('No');
            const usd_currency = item.exchange_changes ? __('Yes') : __('No');
            return item.email.toLowerCase().indexOf(props.search.toLowerCase()) > -1
                || item.id.toString().toLowerCase().indexOf(props.search.toLowerCase()) > -1
                || item.site.host.toLowerCase().indexOf(props.search.toLowerCase()) > -1
                || item.user.name.toLowerCase().indexOf(props.search.toLowerCase()) > -1
                || item.file_format.toLowerCase().indexOf(props.search.toLowerCase()) > -1
                || getLanguageLabel(item.price_language).toLowerCase().indexOf(props.search.toLowerCase()) > -1
                || exchange_changes.toLowerCase().indexOf(props.search.toLowerCase()) > -1
                || usd_currency.toLowerCase().indexOf(props.search.toLowerCase()) > -1
            // || item.generated_at ? moment(item.generated_at).locale('uk').calendar().toLowerCase().indexOf(props.search.toLowerCase()) > -1 : false
            // || moment(item.created_at).locale('uk').format('DD.MM.YYYY HH:mm').toLowerCase().indexOf(props.search.toLowerCase()) > -1
            // || item.config_versions.length > 0 ? item.config_versions.slice(-1)[0].user.name.toLowerCase().indexOf(props.search.toLowerCase()) > -1 || moment(item.updated_at).locale('uk').format('DD.MM.YYYY HH:mm').toLowerCase().indexOf(props.search.toLowerCase()) > -1 : moment(item.updated_at).locale('uk').format('DD.MM.YYYY HH:mm').toLowerCase().indexOf(props.search.toLowerCase()) > -1
        }))
        const isModalActive = ref(false)
        const itemForDeleteId = ref(false)
        const isGenerationModalActive = ref(false)
        const perPage = ref(10)
        const currentPage = ref(0)
        const checkedRows = ref([])

        const itemsPaginated = computed(
            () => items.value.slice(perPage.value * currentPage.value, perPage.value * (currentPage.value + 1))
        )

        const numPages = computed(() => Math.ceil(items.value.length / perPage.value))

        const remove = (arr, cb) => {
            const newArr = []
            arr.forEach(item => {
                if (!cb(item)) {
                    newArr.push(item)
                }
            })
            return newArr
        }

        const checked = (isChecked, client) => {
            if (isChecked) {
                checkedRows.value.push(client)
            } else {
                checkedRows.value = remove(checkedRows.value, row => row.id === client.id)
            }
        }

        const isRunningUpdatePricesJob = ref(false)

        return {
            isModalActive,
            itemForDeleteId,
            currentPage,
            numPages,
            checkedRows,
            itemsPaginated,
            checked,
            mdiEye,
            mdiTrashCan,
            mdiPencil,
            mdiDownload,
            mdiLinkVariant,
            mdiChevronLeft,
            mdiChevronRight,
            moment,
            isGenerationModalActive,
            getLanguageLabel,
            isRunningUpdatePricesJob
        }
    }
}
</script>
